import { getFileExtension, getInlineViewerUrl, getViewerType } from '@cognitoforms/file-viewer/src/file-viewer.types';
import type {
	FileViewerField,
	FileViewerManifest,
	FileViewerFile
} from '@cognitoforms/file-viewer/src/file-viewer.types';
import type { FileDataRef } from '@cognitoforms/types/server-types/model/file-data-ref';
import type { EntryDocumentAttachment, EntryUploadAttachment } from '../util/entry-files';
import type { FileViewerGeneration } from './use-file-viewer-generation';

export interface FileViewerHostFile extends Pick<FileDataRef, 'Id' | 'Name' | 'Size' | 'ContentType'> {
	downloadUrl: string;
	viewerUrl: string;
	thumbnailUrl?: string;
	requiresGeneration?: boolean;
	generatedFormId?: string;
	generatedFieldName?: string;
}

export interface DocumentHostFileSource {
	File: Pick<FileDataRef, 'Id' | 'Name' | 'Size' | 'ContentType'>;
	DocumentName?: string | null;
}

export function toDocumentHostFile(document: DocumentHostFileSource, downloadUrl: string, fallbackName = ''): FileViewerHostFile {
	const file = document.File;

	return {
		Id: file.Id,
		Name: file.Name || document.DocumentName || fallbackName,
		Size: file.Size,
		ContentType: file.ContentType || 'application/pdf',
		downloadUrl,
		viewerUrl: getInlineViewerUrl(downloadUrl)
	};
}

export function toUploadHostFile(file: FileDataRef, downloadUrl: string): FileViewerHostFile {
	return {
		Id: file.Id,
		Name: file.Name || 'File',
		Size: file.Size || 0,
		ContentType: file.ContentType || '',
		downloadUrl,
		viewerUrl: getInlineViewerUrl(downloadUrl)
	};
}

export interface FileViewerHost {
	id: string;
	getFileViewerFieldLabel: () => string;
	getFileViewerFiles: (options?: BuildFileViewerManifestOptions) => Promise<FileViewerHostFile[]>;
	isFileViewerVisible?: () => boolean;
	generateFileViewerFile?: (file: FileViewerFile) => Promise<FileViewerGeneration>;
}

export interface BuildFileViewerManifestOptions {
	includeHidden?: boolean;
}

export interface SignedDocumentAttachment {
	fieldId: string;
	fieldLabel: string;
	file: FileViewerHostFile;
	templateNumber?: number;
	signing?: unknown;
}

function buildFileViewerFileRow(
	hostFile: FileViewerHostFile,
	fieldId: string,
	fieldLabel: string,
	fieldIndex: number,
	globalIndex: number
): FileViewerFile {
	return {
		Id: hostFile.Id,
		Name: hostFile.Name,
		Size: hostFile.Size,
		ContentType: hostFile.ContentType,
		fileId: hostFile.Id,
		fileName: hostFile.Name,
		fileSize: hostFile.Size,
		contentType: hostFile.ContentType,
		fieldId,
		fieldLabel,
		fieldIndex,
		globalIndex,
		downloadUrl: hostFile.downloadUrl,
		viewerUrl: hostFile.viewerUrl,
		thumbnailUrl: hostFile.thumbnailUrl ?? hostFile.downloadUrl,
		extension: getFileExtension(hostFile.Name),
		viewerType: getViewerType({ Name: hostFile.Name, ContentType: hostFile.ContentType }),
		requiresGeneration: hostFile.requiresGeneration,
		generatedFormId: hostFile.generatedFormId,
		generatedFieldName: hostFile.generatedFieldName
	};
}

export async function buildFileViewerManifest(
	hosts: FileViewerHost[],
	options?: BuildFileViewerManifestOptions
): Promise<FileViewerManifest> {
	const includeHidden = options?.includeHidden ?? false;
	const fields: FileViewerField[] = [];
	const files: FileViewerFile[] = [];
	let globalIndex = 0;

	const visibleHosts = hosts.filter(host => includeHidden || (host.isFileViewerVisible ? host.isFileViewerVisible() : true));
	const hostFileLists = await Promise.all(visibleHosts.map(host => host.getFileViewerFiles(options)));

	visibleHosts.forEach((host, hostIndex) => {
		const fieldId = host.id;
		const fieldLabel = host.getFileViewerFieldLabel();
		const fieldFiles = hostFileLists[hostIndex].map((hostFile, fieldIndex) => {
			const row = buildFileViewerFileRow(hostFile, fieldId, fieldLabel, fieldIndex, globalIndex);
			globalIndex += 1;
			return row;
		});

		fields.push({
			fieldId,
			fieldLabel,
			files: fieldFiles
		});
		files.push(...fieldFiles);
	});

	return {
		formTitle: '',
		entryTitle: '',
		fields,
		files
	};
}

function upsertByFileId(files: FileViewerFile[], file: FileViewerFile): FileViewerFile[] {
	const existingIndex = files.findIndex(existing => existing.fileId === file.fileId);
	return existingIndex >= 0
		? files.map((existing, index) => (index === existingIndex ? file : existing))
		: [...files, file];
}

export function mergeFileIntoFileViewerManifest(
	manifest: FileViewerManifest,
	file: FileViewerFile
): FileViewerManifest {
	const files = upsertByFileId(manifest.files, file);

	let matchedGroup = false;
	const fields = manifest.fields.map(group => {
		if (group.fieldId !== file.fieldId) {
			return group.files.some(existing => existing.fileId === file.fileId)
				? { ...group, files: group.files.filter(existing => existing.fileId !== file.fileId) }
				: group;
		}

		matchedGroup = true;
		return { ...group, files: upsertByFileId(group.files, file) };
	});

	if (!matchedGroup) {
		fields.push({
			fieldId: file.fieldId,
			fieldLabel: file.fieldLabel,
			files: [file]
		});
	}

	return {
		...manifest,
		fields,
		files
	};
}

export function appendSignedDocumentAttachments(
	manifest: FileViewerManifest,
	attachments: SignedDocumentAttachment[]
): FileViewerManifest {
	let nextManifest = manifest;

	for (const attachment of attachments) {
		const existingGroup = nextManifest.fields.find(group => group.fieldId === attachment.fieldId);
		const fieldIndex = existingGroup ? existingGroup.files.length : 0;
		const globalIndex = nextManifest.files.length;

		const row: FileViewerFile = {
			...buildFileViewerFileRow(attachment.file, attachment.fieldId, attachment.fieldLabel, fieldIndex, globalIndex),
			templateNumber: attachment.templateNumber,
			signing: attachment.signing
		};

		nextManifest = mergeFileIntoFileViewerManifest(nextManifest, row);
	}

	return nextManifest;
}

export async function appendEntryDocumentAttachments(
	manifest: FileViewerManifest,
	documents: EntryDocumentAttachment[],
	getDownloadUrl: (file: FileDataRef) => Promise<string> | string
): Promise<FileViewerManifest> {
	const listedFileIds = new Set(manifest.files.map(file => file.fileId));

	const attachments = await Promise.all(documents
		.filter(({ document }) => !listedFileIds.has(document.File.Id))
		.map(async ({ property, document }): Promise<SignedDocumentAttachment> => {
			const downloadUrl = await getDownloadUrl(document.File);
			const fieldLabel = property.label || property.name;

			return {
				fieldId: `${property.containingType.fullName}.${property.name}`,
				fieldLabel,
				file: toDocumentHostFile(document, downloadUrl, fieldLabel)
			};
		}));

	return appendSignedDocumentAttachments(manifest, attachments);
}

export async function appendEntryUploadAttachments(
	manifest: FileViewerManifest,
	uploads: EntryUploadAttachment[],
	getDownloadUrl: (file: FileDataRef) => Promise<string> | string
): Promise<FileViewerManifest> {
	const listedFileIds = new Set(manifest.files.map(file => file.fileId));
	const unlistedUploads = uploads.filter(({ file }) => {
		if (listedFileIds.has(file.Id))
			return false;

		listedFileIds.add(file.Id);
		return true;
	});

	const attachments = await Promise.all(unlistedUploads.map(async ({ property, file }): Promise<SignedDocumentAttachment> => ({
		fieldId: `${property.containingType.fullName}.${property.name}`,
		fieldLabel: property.label || property.name,
		file: toUploadHostFile(file, await getDownloadUrl(file))
	})));

	return appendSignedDocumentAttachments(manifest, attachments);
}
